import React, { useCallback, useEffect, useMemo } from 'react';
import BaseModalComponent, { StyledBaseModal } from '../BaseModal.component';
import { useTranslation } from 'react-i18next';
import styled, { useTheme } from 'styled-components';
import SpinnerComponent, { StyledSpinnerView } from '../../Spinner.component';
import { useAppDispatch } from '../../../hooks/useAppDispatcher.hook';
import { useAppSelector } from '../../../hooks/useAppSelector.hook';
import FormLayoutComponent from '../../Form/FormLayout.component';
import InputComponent from '../../Input/Input.component';
import SelectComponent from '../../Input/Select.component';
import {
  getCandidateSelected,
  getIsEditCandidate,
  resetCandidateSelectedAction,
  setCandidateSelectedAction,
  setIsEditCandidateAction,
} from '../../../redux/Candidate/Candidate.slice';
import {
  checkPersonThunk,
  getCandidateInfoThunk,
  updatePersonThunk,
} from '../../../redux/Candidate/Candidate.thunk';
import {
  maxLength,
  minLength,
  regExp,
  required,
} from '../../../services/validator.service';
import { REGEXP } from '../../../constants/regexp.constant';
import { Person } from '../../../redux/Candidate/Candidate.type';
import CardComponent, { StyledCard } from '../../Card.component';
import PersonEditModalLinksBlockComponent from '../../Blocks/PersonEditModalLinksBlock.component';
import ButtonComponent, { StyledButton } from '../../Button.component';
import {
  PersonCreateFetch,
  PersonUpdateFetch,
} from '../../../redux/Candidate/Experience/PersonExperience.type';
import ModalAttachBlockComponent from '../../Blocks/ModalAttachBlock.component';
import { useModalManager } from '../../../hooks/ModalManager.hook';
import ApiWrapper from '../../../hooks/useApi.hook';
import RecordPreNewModalComponent from './Record/RecordPreNewModal.component';
import { Transition } from 'react-transition-group';
import TagBlock from '../../Blocks/TagBlock';
import { useInternship } from '../../../hooks/useInternship';
import ErrorAlertComponent from '../../Alert/ErrorAlert.component';
import { AxiosError } from 'axios';
import { ERROR_CODE } from '../../../constants/error.constant';
import useFormHook from '../../../hooks/useForm.hook';
import { phoneNumberFormatter } from '../../../utils/formatters';
import useEnglishLevel from '../../../hooks/useEnglishLevel.hook';
import useExperienceHook from '../../../hooks/useExperience.hook';
import {
  deleteLocalStorage,
  getLocalStorage,
  setLocalStorage,
} from '../../../utils/storage';
import {
  AttachEditState,
  Attachment,
} from '../../../redux/Attachment/Attachment.type';
import { ARTDECO_MODAL_OUTLET } from '../../../constants/extension.constant';
import { Tag } from '../../../redux/ControlPanel/TagControl/TagControl.type';

export interface CandidateEditModalProps
  extends React.AllHTMLAttributes<HTMLDivElement> {
  info?: Person;
  onNext?: () => void;
  isInternship?: boolean;
  isCandidatePage?: boolean;
  onCandidateAdded?: () => void;
}

type ResponseData = {
  message?: string;
};

const StyledPersonEditModalFooter = styled.div`
  display: flex;
  justify-content: flex-end;
  margin: 10px 0 0 0;
  gap: 10px;

  ${StyledButton} {
    min-width: 100px;
  }
`;
const StyledAttachModal = styled.div`
  display: flex;
  animation: ${({ theme }) => theme.animations.slide.modal} 0.2s ease-in-out
    forwards;

  ${StyledCard} {
    width: 300px;
    height: auto;
  }
`;
const StyledCandidateLinks = styled.div`
  display: flex;
  animation: ${({ theme }) => theme.animations.slide.modal} 0.2s ease-in-out
    forwards;

  ${StyledCard} {
    width: 300px;
    height: auto;
  }
`;
const StyledCandidateEditModalStack = styled.div`
  display: grid;
  gap: 15px;
  align-items: center;
  padding: 10px 0;
`;
const StyledCandidateEditModalBody = styled.div`
  z-index: 400;
`;
const StyledCandidateEditModal = styled.div`
  ${StyledBaseModal} {
    width: 400px;
  }

  ${StyledSpinnerView} {
    position: absolute;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
    background: ${({ theme }) => theme.colors.CARD};
    border-radius: 8px;
  }
`;

const StyledHeaderTitle = styled.div`
  overflow: hidden;
  text-overflow: ellipsis;
`;

const PersonEditModalComponent = ({
  info,
  isInternship,
  isCandidatePage,
  onCandidateAdded,
}: CandidateEditModalProps) => {
  const theme = useTheme() as any;
  const dispatch = useAppDispatch();
  const { openModal } = useModalManager();
  const record = useAppSelector(getCandidateSelected);
  const isEditCandidate = useAppSelector(getIsEditCandidate);
  const { t } = useTranslation();
  const { closeModal } = useModalManager();
  const { getSelectList: getEnglishLevelList } = useEnglishLevel();
  const { getSelectList: getExperienceList } = useExperienceHook();

  let savedEntity = 'newPerson';
  const isLinkedin = window.location.origin.includes('linkedin');

  if (isLinkedin) {
    const person = window.location.pathname.split('/')[2];
    savedEntity = `editPersonL-${person}`;
  }
  if (isEditCandidate) {
    savedEntity = `editPerson-${record?.item?.id}`;
  }

  const savedData = getLocalStorage(savedEntity) || '';
  const parsedSavedData = savedData ? JSON.parse(savedData) : {};

  const { form, registerField, setFormValue, validate, handleFormChange } =
    useFormHook({
      lastName: '',
      firstName: '',
      email: '',
      phone: '',
      experience: undefined,
      english: undefined,
      links: [],
      attachments: [],
      tags: [],
    });

  const { savePersonResult } = useInternship();

  useEffect(() => {
    if (isLinkedin) {
      dispatch(setIsEditCandidateAction(false));
      dispatch(resetCandidateSelectedAction(undefined));
    }
  }, []);

  useEffect(() => {
    if (!info || (info && !info.id)) return;
    dispatch(getCandidateInfoThunk(info!.id));
  }, [dispatch]);

  useEffect(() => {
    if (!info) return;
    setFormValue({
      ...info,
      phone: phoneNumberFormatter(info.phone ?? ''),
    });
  }, [info]);

  useEffect(() => {
    if (!record.item || isLinkedin) return;

    const { item } = record;

    const newForm = {
      ...item,
      phone: phoneNumberFormatter(item.phone ?? ''),
      attachments: parsedSavedData.attachments || item.attachments,
      links: parsedSavedData.links || item.links,
      experience: item?.experience && {
        value: form.experience?.value || item.experience.id,
        label: form.experience?.label || item.experience.name,
      },
      english: item?.english && {
        value: form.english?.value || item.english.id,
        label: form.english?.label || item.english.level,
      },
    };

    setFormValue(newForm);
  }, [record]);

  useEffect(() => {
    if (!Object.keys(parsedSavedData).length) return;

    setFormValue({
      lastName: parsedSavedData.lastName || '',
      firstName: parsedSavedData.firstName || '',
      email: parsedSavedData.email || '',
      phone: phoneNumberFormatter(parsedSavedData.phone ?? ''),
      experience: parsedSavedData.experience || undefined,
      english: parsedSavedData.english || undefined,
      links: parsedSavedData.links || [],
      attachments: parsedSavedData.attachments || [],
      tags: parsedSavedData.tags || [],
    });
  }, []);

  useEffect(() => {
    /* это логика нужна для работы расширения для хрома
       при открытии нашей модалки прячем модалку linkedin-а, иначе не работает фокус на полях
       при onmount возвращаем скрытую модалку linkedin-а
    */
    /* класс 'artdeco-modal-is-open' для body нужен чтобы не было полосы прокрутки для расширения в linkedin */
    const artdecoModalOutlet: HTMLElement | null =
      document.querySelector(ARTDECO_MODAL_OUTLET);
    const body = document.querySelector('body');

    if (isLinkedin) {
      body?.classList.add('artdeco-modal-is-open');

      if (artdecoModalOutlet) {
        artdecoModalOutlet.style.display = 'none';
      }
    }

    return () => {
      if (isLinkedin) {
        body?.classList.remove('artdeco-modal-is-open');
        if (artdecoModalOutlet) {
          artdecoModalOutlet.style.display = 'block';
        }
      }
    };
  }, []);

  const checkCustomCodeStatus = (err: AxiosError<ResponseData>) => {
    if (!err.response) {
      return;
    }

    const { status } = err.response;
    if (status === ERROR_CODE.CUSTOM_CODE) {
      return true;
    }
  };

  const saveResult = useCallback(() => {
    if (!validate()) return;

    const data = {
      id: info ? info.id : undefined,
      firstName: form.firstName,
      lastName: form.lastName,
      email: form.email,
      phone: form.phone,
      experience: form.experience,
      english: form.english,
      experienceId: form.experience ? form.experience.value : undefined,
      englishLevelId: form.english ? form.english.value : undefined,
      attachments: form.attachments,
      links: form.links,
      tags: form.tags,
    };

    dispatch(setCandidateSelectedAction({ ...info, ...data, update: true }));

    ApiWrapper(
      checkPersonThunk({
        ...data,
        ...(data.id && { hasSoftSearch: false }),
      } as PersonCreateFetch),
    ).then(([res, err]: [boolean, AxiosError<ResponseData>]) => {
      if (res && isInternship) {
        savePersonResult(data as PersonCreateFetch);
        closeModal();
        return;
      }

      if (!res) {
        !checkCustomCodeStatus(err) &&
          openModal(ErrorAlertComponent, {
            header: t('Error'),
            children: err.response?.data?.message || t('Unknown status'),
          });
      }

      if (res && info?.id) {
        dispatch(
          setCandidateSelectedAction({ ...info, ...data, update: true }),
        );
        dispatch(setIsEditCandidateAction(false));
        ApiWrapper(updatePersonThunk(data as PersonUpdateFetch)).then(() => {
          deleteLocalStorage(savedEntity);
          closeModal();
        });
        return;
      }

      if (res) {
        openModal(RecordPreNewModalComponent, {
          mergePerson: {
            ...data,
          } as any,
          isUnmergeDuplicate: true,
          isNewCandidate: true,
          onCandidateAdded,
        });
      }

      dispatch(setIsEditCandidateAction(!!isCandidatePage));
    });
  }, [info, form]);

  const renderHeader = useMemo(() => {
    if (!record.item || (record.item && !record.item.id))
      return t('New person');
    const { item } = record;

    return <StyledHeaderTitle>{item.fullName}</StyledHeaderTitle>;
  }, [record]);

  const handleCloseModal = () => {
    if (isEditCandidate) {
      dispatch(setIsEditCandidateAction(false));
    } else {
      dispatch(resetCandidateSelectedAction(undefined));
    }
    if (isLinkedin) {
      deleteLocalStorage(savedEntity);
    }

    closeModal();
  };

  const handleFormSave = (data: { [key: string]: any }) => {
    const attrKey = Object.keys(data)[0];
    const attrValue = Object.values(data)[0];

    const newForm = {
      ...form,
      ...parsedSavedData,
      [attrKey]: attrValue,
    };

    setLocalStorage(savedEntity, JSON.stringify(newForm));
  };

  const renderFooter = useMemo(() => {
    return (
      <StyledPersonEditModalFooter>
        <ButtonComponent
          appearance="primary"
          disabled={record.loading}
          handleClick={saveResult}
        >
          {t('Next')}
        </ButtonComponent>
        <ButtonComponent
          handleClick={() => {
            handleCloseModal();
            deleteLocalStorage(savedEntity);
          }}
        >
          {t('Cancel')}
        </ButtonComponent>
      </StyledPersonEditModalFooter>
    );
  }, [record, form]);

  const renderPersonAttachment = useMemo(
    () => (
      <StyledAttachModal>
        <CardComponent isLoading={record.loading}>
          <ModalAttachBlockComponent
            {...registerField('attachments')}
            value={form.attachments || []}
            onChangeAttachments={(attachments: AttachEditState[]) => {
              handleFormChange({ attachments });
              const attachmentsForSave = attachments.filter(
                (item: AttachEditState) => !item.file,
              );
              handleFormSave({ attachments: attachmentsForSave });
            }}
          />
        </CardComponent>
      </StyledAttachModal>
    ),
    [record, form, form.attachments],
  );

  const renderPersonLinks = useMemo(
    () => (
      <StyledCandidateLinks>
        <CardComponent isLoading={record.loading}>
          <PersonEditModalLinksBlockComponent
            {...registerField('links')}
            value={form.links}
            onChangeLinks={(links: Attachment[]) => {
              handleFormChange({ links });
              handleFormSave({ links });
            }}
          />
        </CardComponent>
      </StyledCandidateLinks>
    ),
    [record, form],
  );

  const renderContent = useMemo(
    () => (
      <StyledCandidateEditModalBody>
        <FormLayoutComponent>
          <StyledCandidateEditModalStack>
            <InputComponent
              {...registerField('lastName', {
                validations: [required, minLength(2)],
              })}
              value={form.lastName}
              label={t('Last name') + '*'}
              placeholder={t('Enter last name')}
              onChange={(lastName: string) => {
                handleFormChange({ lastName });
                handleFormSave({ lastName });
              }}
            />

            <InputComponent
              {...registerField('firstName', {
                validations: [required, minLength(2)],
              })}
              value={form.firstName}
              label={t('First name') + '*'}
              placeholder={t('Enter first name')}
              onChange={(firstName: string) => {
                handleFormChange({ firstName });
                handleFormSave({ firstName });
              }}
            />

            <InputComponent
              {...registerField('email')}
              value={form.email}
              label={t('Email')}
              placeholder={t('Enter email')}
              onChange={(email: string) => {
                handleFormChange({ email: email.trim() });
                handleFormSave({ email: email.trim() });
              }}
            />

            <InputComponent
              {...registerField('phone', {
                validations: [
                  regExp(REGEXP.PHONE),
                  minLength(7),
                  maxLength(25),
                ],
              })}
              prefix={<span>+</span>}
              value={form.phone}
              label={t('Phone')}
              placeholder={t('Enter phone')}
              onChange={(phone: string) => {
                handleFormChange({ phone: phoneNumberFormatter(phone) });
                handleFormSave({ phone: phoneNumberFormatter(phone) });
              }}
            />

            <SelectComponent
              {...registerField('experience', {
                validations: [required],
              })}
              value={form.experience}
              label={t('Experience') + '*'}
              placeholder={t('Choose experience')}
              loadOptions={getExperienceList}
              onChangeOption={(experience: {
                label: string;
                value: number;
              }) => {
                handleFormChange({ experience });
                handleFormSave({ experience });
              }}
            />
            <SelectComponent
              {...registerField('english', {
                validations: [required],
              })}
              value={form.english}
              label={t('English level') + '*'}
              placeholder={t('Choose english level')}
              loadOptions={getEnglishLevelList}
              onChangeOption={(english: { label: string; value: string }) => {
                handleFormChange({ english });
                handleFormSave({ english });
              }}
            />

            <TagBlock
              {...registerField('tags')}
              value={form.tags}
              label={t('Tags')}
              onTagsChange={(tags: Tag[]) => {
                handleFormChange({ tags });
                handleFormSave({ tags });
              }}
            />
          </StyledCandidateEditModalStack>
        </FormLayoutComponent>
      </StyledCandidateEditModalBody>
    ),
    [form],
  );

  return (
    <StyledCandidateEditModal>
      <BaseModalComponent
        onCrossButtonClick={handleCloseModal}
        header={renderHeader}
        footer={renderFooter}
        leftBlock={renderPersonAttachment}
        rightBlock={renderPersonLinks}
      >
        {renderContent}
        <Transition in={!!record.loading} unmountOnExit timeout={{ exit: 300 }}>
          {state => (
            <SpinnerComponent
              style={{
                ...theme.animations.reverseDefaultState,
                ...theme.animations.defaultSteps[state],
              }}
            />
          )}
        </Transition>
      </BaseModalComponent>
    </StyledCandidateEditModal>
  );
};

export default PersonEditModalComponent;
